export function applySoaResponseToForm($j, $form, dados, ctx) {
  const { cpfDigits, formatIsoFromBrDate, formatCpf } = ctx

  // =========================
  // Helpers
  // =========================

  const overwrite = !!ctx.overwrite // default: false

  const $find = (selector) => $form.find(selector)

  const getVal = (selector) => {
    const $el = $find(selector)
    if (!$el.length) return null
    return ($el.val() ?? "").toString()
  }

  // Considera "vazio" mesmo se for só espaços, ou máscara sem números (CPF/CEP/telefone)
  const isBlank = (value, { digitsOnly = false } = {}) => {
    if (value == null) return true
    const s = value.toString()
    if (digitsOnly) return s.replace(/\D/g, "").length === 0
    return s.trim().length === 0
  }

  // Decide se pode preencher: se overwrite=true sempre; se não, só se campo estiver vazio
  const canFill = (selector, { digitsOnly = false } = {}) => {
    if (overwrite) return true
    const current = getVal(selector)
    return isBlank(current, { digitsOnly })
  }

  // Highlight rápido para mostrar o que foi preenchido automaticamente
  const flashFilled = ($el) => {
    // evita jogar classes em inputs de componentes muito específicos
    $el.addClass("soa-autofilled")
    setTimeout(() => $el.removeClass("soa-autofilled"), 1400)
  }

  // Preenche com triggers, mas só se existir e (por padrão) estiver vazio
  const setVal = (selector, value, { digitsOnly = false } = {}) => {
    if (value == null) return false
    const v = value.toString()
    if (v.trim() === "") return false

    const $el = $find(selector)
    if (!$el.length) return false
    if (!canFill(selector, { digitsOnly })) return false

    $el
      .val(v)
      .trigger("input")
      .trigger("change")

    flashFilled($el)
    return true
  }

  // Para selects (ex: sexo M/F) — tenta setar e dar change
  const setSelect = (selector, value) => {
    if (value == null) return false
    const v = value.toString()
    if (v.trim() === "") return false

    const $el = $find(selector)
    if (!$el.length) return false
    if (!canFill(selector)) return false

    // se a opção não existir, não seta pra não ficar “quebrado”
    const hasOption = $el.find(`option[value="${cssEscape(v)}"]`).length > 0
    if (!hasOption) return false

    $el.val(v).trigger("change")
    flashFilled($el)
    return true
  }

  // CSS.escape fallback (pra value com caracteres especiais)
  function cssEscape(str) {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(str)
    return String(str).replace(/["\\]/g, "\\$&")
  }

  // Monta endereço “bonito”
  const buildEnderecoCompleto = (logradouro, numero, complemento) => {
    const base = [logradouro, numero].filter(Boolean).join(", ")
    return complemento ? `${base} - ${complemento}` : base
  }

  // =========================
  // DADOS BÁSICOS
  // =========================

  setVal("#params_nome_candidato", dados.nome)

  setVal("#params_nome_social", dados.nome_social)

  setVal("#params_nome_mae", dados.nome_mae)

  // CPF no form (se existir) — não sobrescreve se já tiver preenchido
  const cpfFmt = formatCpf(dados.documento || cpfDigits)
  setVal("#params_cpf", cpfFmt, { digitsOnly: true })

  // Data nascimento (SOA dd/mm/aaaa -> input text dd/mm/aaaa)
  if (dados.data_nascimento) {
    setVal("#params_data_nascimento", dados.data_nascimento)
  }

  // Sexo: SOA vem "Masculino"/"Feminino"
  if (dados.sexo) {
    const sexo = dados.sexo.toString().toLowerCase()
    if (sexo.includes("masc")) setSelect("#params_sexo", "M")
    if (sexo.includes("fem")) setSelect("#params_sexo", "F")
  }

  setVal("#params_escolaridade", dados.escolaridade)

  setVal("#params_atividade_profissional", dados.atividade_profissional)

  // =========================
  // TELEFONE (prioriza celular)
  // =========================

  // Se o SOA mandar "telefone" direto
  if (dados.telefone) {
    setVal("#params_telefone", dados.telefone, { digitsOnly: true })
  } else if (dados.telefones && dados.telefones.telefone) {
    let telefones = dados.telefones.telefone
    if (!Array.isArray(telefones)) telefones = [telefones]

    const pickCelular =
      telefones.find(t => (t.tipo_telefone || "").toString().toLowerCase().includes("celular")) ||
      telefones[0]

    if (pickCelular && pickCelular.numero) {
      setVal("#params_telefone", pickCelular.numero, { digitsOnly: true })
    }
  }

  // =========================
  // EMAIL
  // =========================

  if (dados.email) {
    setVal("#params_email", dados.email)
  } else if (dados.emails && dados.emails.email && dados.emails.email.email) {
    setVal("#params_email", dados.emails.email.email)
  }

  // =========================
  // ENDEREÇO (pega 1º residencial)
  // =========================

  if (dados.enderecos && dados.enderecos.endereco) {
    let enderecos = dados.enderecos.endereco
    if (!Array.isArray(enderecos)) enderecos = [enderecos]

    const residencial =
      enderecos.find(e => (e.tipo || "").toString().toLowerCase().includes("resid")) ||
      enderecos[0]

    if (residencial) {
      const logradouro = (residencial.logradouro || "").toString().trim()
      const numero = (residencial.numero || "").toString().trim()
      const complemento = (residencial.complemento || "").toString().trim()
      const bairro = (residencial.bairro || "").toString().trim()
      const cidade = (residencial.cidade || "").toString().trim()
      const estado = (residencial.estado || "").toString().trim()
      const cep = (residencial.cep || "").toString().trim()

      // Se o form tiver campos separados
      setVal("#params_logradouro", logradouro)
      setVal("#params_numero", numero)
      setVal("#params_complemento", complemento)
      setVal("#params_bairro", bairro)
      setVal("#params_cep", cep, { digitsOnly: true })
      setVal("#params_estado", estado)
      setVal("#params_uf", estado) // fallback comum em alguns forms

      // Município pode ser simples ou auto_complete (aqui só seta o input)
      setVal("#params_municipio", cidade)

      // Campo único "endereco" (fallback)
      const enderecoCompleto = buildEnderecoCompleto(logradouro, numero, complemento)
      setVal("#params_endereco", enderecoCompleto)
    }
  }

  // =========================
  // PARTICIPAÇÃO SOCIETÁRIA
  // =========================

  if (dados.participacoes && dados.participacoes.participacao) {
    const part = dados.participacoes.participacao
    if (part && part.nome) setVal("#params_empresa_vinculada", part.nome)
    if (part && part.documento) setVal("#params_cnpj_empresa_vinculada", part.documento, { digitsOnly: true })
  }

  // =========================
  // Pequena melhoria: se quiser, loga no console só em dev
  // =========================
  // console.debug("[SOA][mapper] aplicado", { overwrite, keys: Object.keys(dados || {}) })
}
